import { useEffect, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { cn } from "@/shared/utils/general/cn"
import type { LoaderPhase } from "./useInitialSyncReady"

/**
 * InitialSyncLoader — full-screen loading overlay shown during initial sync.
 *
 * Displayed after OTP/OAuth completes (Step 2 in DatabaseProvider) while
 * Dexie Cloud runs initial sync. Covers both new users (empty IDB) and
 * returning users (40K+ objects). Without this overlay, returning users
 * see an empty sidebar and skeleton tasks for ~30s during sync.
 *
 * Follows world-class loading patterns:
 * - Phase-aware messages (not timer-based): tied to real sync state
 * - Indeterminate progress bar (Dexie Cloud has no progress %)
 * - Smooth 300ms fade-out when dismissed
 * - Minimum 1.5s display (handled by useInitialSyncReady)
 * - Maximum 5min display (handled by useInitialSyncReady)
 *
 * Usage: rendered by DatabaseProvider after login (Step 2).
 */

interface InitialSyncLoaderProps {
  /** Current sync phase from useInitialSyncReady */
  phase: LoaderPhase
  /** Whether the loader is fading out (sync done, animating away) */
  isFadingOut?: boolean
}

/** Map sync phases to user-facing messages */
function getPhaseMessage(
  phase: LoaderPhase,
  t: (key: string) => string,
): { title: string; subtitle: string } {
  switch (phase) {
    case "initial":
    case "not-in-sync":
      return {
        title: t("Connecting to the cloud"),
        subtitle: t("Preparing your data"),
      }
    case "pushing":
      return {
        title: t("Syncing your data"),
        subtitle: t("Almost there"),
      }
    case "pulling":
      return {
        title: t("Downloading your data"),
        subtitle: t("This may take a few seconds"),
      }
    case "in-sync":
      return {
        title: t("All set!"),
        subtitle: t("Your data is ready"),
      }
    case "error":
    case "offline":
      return {
        title: t("Starting in offline mode"),
        subtitle: t("Your data will sync when connected"),
      }
    case "timeout":
      return {
        title: t("Almost ready"),
        subtitle: t("Finishing up"),
      }
    default:
      return {
        title: t("Loading"),
        subtitle: t("Please wait"),
      }
  }
}

export function InitialSyncLoader({ phase, isFadingOut }: InitialSyncLoaderProps) {
  const { t } = useTranslation()
  const [mounted, setMounted] = useState(false)
  // Freeze the message when fading out — don't flash "All set!" during exit
  const lastActivePhaseRef = useRef<LoaderPhase>(phase)
  if (!isFadingOut) {
    lastActivePhaseRef.current = phase
  }
  const message = getPhaseMessage(lastActivePhaseRef.current, t)

  // Trigger entrance animation
  useEffect(() => {
    const raf = requestAnimationFrame(() => setMounted(true))
    return () => cancelAnimationFrame(raf)
  }, [])

  return (
    <div
      className={cn(
        "flex h-screen flex-col items-center justify-center bg-background transition-all duration-300",
        mounted && !isFadingOut && "opacity-100 scale-100",
        !mounted && "opacity-0 scale-105",
        isFadingOut && "opacity-0 scale-95 pointer-events-none",
      )}
    >
      {/* Indeterminate progress bar — gradient shimmer matching SyncStatusProgressBar */}
      <div
        className={`relative mb-8 h-1 w-48 overflow-hidden rounded-full ${isFadingOut ? 'bg-muted' : ''}`}
        style={!isFadingOut ? {
          maskImage: "linear-gradient(to right, transparent, black 10%, black 90%, transparent)",
          WebkitMaskImage: "linear-gradient(to right, transparent, black 10%, black 90%, transparent)",
        } : undefined}
      >
        {isFadingOut ? (
          <div className="h-full w-full rounded-full bg-primary transition-all duration-300" />
        ) : (
          <>
            <div
              className="absolute top-0 bottom-0 animate-indeterminate-bar1"
              style={{
                background: "linear-gradient(to right, transparent 0%, var(--primary) 50%, transparent 100%)",
                opacity: 0.7,
              }}
            />
            <div
              className="absolute top-0 bottom-0 animate-indeterminate-bar2"
              style={{
                background: "linear-gradient(to right, transparent 0%, var(--primary) 50%, transparent 100%)",
                opacity: 0.7,
              }}
            />
          </>
        )}
      </div>

      {/* Phase-aware messages */}
      <p className="text-sm font-medium text-foreground transition-all duration-200">
        {message.title}
      </p>
      <p className="mt-1 text-xs text-muted-foreground transition-all duration-200">
        {message.subtitle}
      </p>
    </div>
  )
}
